/**
 * Stochastic Oscillator
 *
 * Momentum indicator comparing closing price to high-low range over `period`.
 * %K = (Close − Lowest Low) / (Highest High − Lowest Low) × 100
 * %D = SMA(K, smoothK).
 * Default: period=14, smoothK=3, smoothD=3.
 *
 * Category: Momentum | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeSMA } from "./sma";

export const stochasticIndicator: IndicatorDefinition = {
  id: "stoch",
  name: "Stochastic Oscillator",
  shortName: "Stoch",
  category: "momentum",
  pane: "separate",
  params: [
    { key: "kPeriod", label: "%K Period", type: "number", default: 14, min: 1, max: 200 },
    { key: "dPeriod", label: "%D Period", type: "number", default: 3, min: 1, max: 100 },
    { key: "smooth", label: "Smooth", type: "number", default: 3, min: 1, max: 100 },
  ],
  plots: [
    { key: "k", label: "%K", color: "#2196F3", style: "line", lineWidth: 2, priceScaleId: "stoch" },
    { key: "d", label: "%D", color: "#FF5722", style: "line", lineWidth: 1, priceScaleId: "stoch" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { kPeriod, dPeriod, smooth } = params;
    const rawK: number[] = [];

    // Raw %K
    for (let i = 0; i < candles.length; i++) {
      if (i < kPeriod - 1) { rawK.push(NaN); continue; }
      let highest = -Infinity, lowest = Infinity;
      for (let j = i - kPeriod + 1; j <= i; j++) {
        highest = Math.max(highest, candles[j].high);
        lowest = Math.min(lowest, candles[j].low);
      }
      const range = highest - lowest;
      rawK.push(range === 0 ? 50 : ((candles[i].close - lowest) / range) * 100);
    }

    // Smooth %K
    const smoothedK = computeSMA(rawK.filter(v => !isNaN(v)), smooth);
    const kData: { time: number; value: number }[] = [];
    let validIdx = 0;
    for (let i = 0; i < rawK.length; i++) {
      if (isNaN(rawK[i])) continue;
      if (!isNaN(smoothedK[validIdx])) {
        kData.push({ time: candles[i].time, value: smoothedK[validIdx] });
      }
      validIdx++;
    }

    // %D = SMA of smoothed %K
    const kValues = kData.map(d => d.value);
    const dValues = computeSMA(kValues, dPeriod);
    const dData = dValues
      .map((v, i) => ({ time: kData[i]?.time || 0, value: v }))
      .filter(d => !isNaN(d.value) && d.time > 0);

    return [
      { key: "k", data: kData },
      { key: "d", data: dData },
    ];
  },
};
